import React, { Fragment, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { useMediaQuery } from 'react-responsive'
import { translate } from '../Services/translation';
import { tcEventClickNavigationEvent, tcEventClickBrandCornerEvent } from "../Actions/TagCommander.action"
import { toggleScrollLock } from '../Services/helpers';

const Navigation = ({ categoryLinks = [], contentLinks = [], uspList = [], socialsList = [], myPage = null, level = 0, onToggle, isAuthenticated }) => {
    const isMobile = useMediaQuery({ maxWidth: 1024 })
    const menuRef = useRef(null);
    const menuButtonRef = useRef(null);
    const menuBackdropRef = useRef(null);
    const [isClosed, setIsClosed] = useState(true);

    function toggleMenu() {
        isClosed
         ? onOpenMenu()
         : onCloseMenu()

        setIsClosed(!isClosed);
        // onToggle && onToggle();
    }

    function onOpenMenu() {
        menuRef.current.toggleAttribute('open')
        menuRef.current.removeAttribute('closed')
        menuButtonRef.current.classList.add('navbar__toggle--open');
        menuBackdropRef.current.classList.add('navbar__backdrop--open')
        menuBackdropRef.current.classList.remove('navbar__backdrop--closed')
        toggleScrollLock(true);
    }

    function onCloseMenu() {
        menuRef.current.removeAttribute('open')
        menuRef.current.toggleAttribute('closed')
        menuButtonRef.current.classList.remove('navbar__toggle--open');
        menuBackdropRef.current.classList.add('navbar__backdrop--closed')
        menuBackdropRef.current.classList.remove('navbar__backdrop--open')
        toggleScrollLock(false);
    }

    const categoryIsSelected = categoryLinks.some(link => link.isSelected);
    const props = { level, categoryLinks, contentLinks, uspList, socialsList, myPage, menuRef, menuButtonRef, toggleMenu, isClosed, isAuthenticated }

    /* Caches renders of components if they are not changed. We dont want the setIsClose state change to recalculate all child components */
    const NavigationMobileMemo = useMemo(() => <NavigationMobile {...{ ...props }} />, [isClosed]);
    const NavigationDesktopMemo = useMemo(() => <NavigationDesktop {...{ ...props }} />, [isClosed]);

    return (
        <Fragment>
            {
                isMobile
                    ? NavigationMobileMemo
                    : NavigationDesktopMemo
            }

            {
                <div className="navbar__backdrop" 
                    ref={menuBackdropRef}
                    onClick={() => toggleMenu()}></div>
            }
        </Fragment>
    );
};

const NavigationMobile = ({ level, categoryLinks, contentLinks, socialsList, myPage, menuRef, menuButtonRef, toggleMenu, isClosed, isAuthenticated }) => {
    useLayoutEffect(() => {
        let vh = window.innerHeight * 0.01;
        menuRef.current.style.setProperty('--menu-vh-unit', `${vh}px`);

        window.addEventListener('resize', () => {
            // We dynamically change the --cart-vh-unit custom prop to calculate the correct height for the cart, 
            // since mobile browsers behave very differently, and often change their viewport on scroll, which
            // can cause the cart to be too small or too large.
            vh = window.innerHeight * 0.01;
            menuRef.current.style.setProperty('--menu-vh-unit', `${vh}px`);
        });
    }, [])

    return (
        <Fragment>
            <button className={`navbar__toggle`} 
                ref={menuButtonRef} 
                onClick={toggleMenu}>

                <div className='toggle-icon'>
                    <div className="toggle-icon__inner"></div>
                </div>

                <span className='icon-label'>
                    {
                        isClosed
                            ? translate('navigation.menu')
                            : translate('navigation.close')
                    }
                </span>
            </button>

            <div className={`navbar-menu__outer ${isClosed ? 'navbar-menu__outer--closed' : ''}`} ref={menuRef}>
                <div className="navbar-menu">
                    <div className="navbar-menu__inner">
                        <div className="submenu">
                            <NavigationItemMobile 
                                level={level}
                                links={[{
                                    name: translate('navigation.products'),
                                    level: 0,
                                    url: null,
                                    links: categoryLinks,
                                    isOpen: categoryLinks.some(link => link.isSelected),
                                    isSelected: categoryLinks.some(link => link.isSelected)
                                }]} />

                            {
                                contentLinks.length > 0 && 
                                    <NavigationItemMobile 
                                        links={contentLinks} 
                                        level={level} />
                            }
                            
                            {
                                isAuthenticated 
                                    ? myPage && <a href={myPage.href} className='submenu__link submenu__link--my-page'>{myPage.text}</a>
                                    : <button data-login-toggle className='submenu__link submenu__link--login'>{translate('login.loginbuttontext')}</button>
                                }

                            {
                                socialsList.length > 0 && (
                                    <ul className="submenu__socials">
                                        {
                                            socialsList.map((social, i) => (
                                                <li className='submenu__social' key={i} dangerouslySetInnerHTML={{ __html: social.value }}></li>
                                            ))
                                        }
                                    </ul>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

const NavigationDesktop = ({ level, categoryLinks, contentLinks, uspList, menuRef, menuButtonRef, toggleMenu }) => {
    const [activeCategory, setActiveCategory] = useState(categoryLinks.find(link => link.isSelected) || categoryLinks[0]);

    const showExpand = categoryLinks != null && categoryLinks.length > 0;

    return (
        <Fragment>
            {
                showExpand && (
                    <button className={`navbar__link navbar__toggle`} 
                        ref={menuButtonRef} 
                        onClick={toggleMenu}>
            
                        <span className='icon-label'>
                            {
                                translate('navigation.products')
                            }
                        </span>
            
                        <div className='toggle-icon'>
                            <div className="toggle-icon__inner"></div>
                        </div>
                    </button>
                )
            }

            {
                contentLinks.length > 0 && contentLinks.map((link, i) => (
                    <a 
                        href={link.url || '#'}
                        className={`navbar__link ${link.isSelected ? 'navbar__link--selected' : ''}`} 
                        key={`${link.name}-${i}`}
                        onClick={ () => { tcEventClickNavigationEvent(link.url) }}>
                        <span dangerouslySetInnerHTML={{ __html: link.name }} />
                    </a>
                ))
            }
    
            <div className='navbar-menu__outer' ref={menuRef}>
                <div className="navbar-menu">
                    <div className="navbar-menu__inner">
                        <div className="submenu">    
                            <ul className="submenu__menu" level={level} open={true}>
    
                                {
                                    categoryLinks.map((link, i) => {
                                        const isSelected = activeCategory === link;
                                        
                                        return (
                                            <li key={i}
                                                className={`submenu__item ${isSelected ? 'submenu__item--selected' : ''}`}
                                                level={0}>
                                                <button className='submenu__expand' onClick={() => { setActiveCategory(link); tcEventClickNavigationEvent(link.url); }}>
                                                    {
                                                        link.imageUrl && <img src={link.imageUrl} alt={link.imageAlt || link.name} className='submenu__icon' />
                                                    }
                                                    {
                                                        link.name && <span className='submenu__title'>{link.name}</span>
                                                    }
                                                    {
                                                        link.extraInfo && <span className='submenu__tagg' dangerouslySetInnerHTML={{ __html: link.extraInfo }}></span>
                                                    }
                                                    <span className='submenu__expand--icon'></span>
                                                </button>
                                            </li>
                                            )
                                        }
                                    )
                                }
    
                            </ul>

                            {
                                activeCategory != null && (
                                    <>
                                        <div className="submenu__desktop">
                                            <a  href={activeCategory.url}
                                                className='submenu__link submenu__link--see-all' 
                                                onClick={() => { tcEventClickNavigationEvent(activeCategory.url)} }>
                                                {
                                                    translate('navigation.categoryPrefix') != null && translate('navigation.categoryPrefix') != ""
                                                        ? `${translate('navigation.categoryPrefix')} ${activeCategory.name}`
                                                        : `Se alla ${activeCategory.name}`
                                                }
                                            </a>

                                            {
                                                activeCategory.links && (
                                                    <NavigationItemDesktop {...{ 
                                                        levelName: activeCategory.name, 
                                                        links: activeCategory.links, 
                                                        extraInfo: activeCategory.extraInfo,
                                                        level: 1,
                                                        showExpand: false,
                                                        showGoBack: false 
                                                    }}/>
                                                )
                                            }
                                        </div>

                                        {
                                            activeCategory.content && (
                                                <div className="submenu__content">   
                                                    {
                                                        activeCategory.content.map((content, i) => (
                                                            <a href={content.url} key={i} onClick={() => { tcEventClickBrandCornerEvent('menu', content.url)} }>
                                                                <div className='submenu__figure'>
                                                                    <img src={content.imageUrl} alt={content.title} loading='lazy' />
                                                                    {content.taggText && <span className='submenu__image-tag'>{content.taggText}</span>}
                                                                    <img src={content.iconUrl} alt={content.iconAlt} className='submenu__image-icon' />
                                                                </div>
                                                                <p>{content.title}</p>
                                                            </a>
                                                        ))
                                                    }  
                                                </div>
                                            )
                                        }
                                    </>
                                )
                            }
                        </div>

                        {
                            uspList.length > 0 && (
                                <div className="submenu__usplist">
                                    {
                                        uspList.map((usp, i) => 
                                            <span key={i} dangerouslySetInnerHTML={{ __html: usp.value }} />
                                        )
                                    }
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

const NavigationItemMobile = ({ levelName, parentLevelName, links, content = [], level, isOpen, closeNavigation, showExpand = true, showGoBack = true }) => {
    const submenuRef = useRef(null);

    return (
        <ul className="submenu__menu" level={level} open={isOpen} ref={submenuRef}>
            {
                level != 0 && showGoBack &&
                    <>
                        <li className='submenu__item submenu__item--back'>
                            <button className='submenu__link submenu__link--back' onClick={() => closeNavigation()}>
                                {
                                    level == 1 
                                        ? translate('navigation.backHome')
                                        : `${translate('navigation.back')} ${parentLevelName}`
                                }
                            </button>
                        </li>

                        <li className='submenu__item submenu__item--title'>
                            { levelName }
                        </li>
                    </>
            }

            {
                links.map((link, i) => {
                    const { links, name, url, imageUrl, imageAlt, extraInfo, itemClass, linkClass } = link;
                    const [isOpen, setIsOpen] = useState(link.isSelected)
                    const itemClassList = ['submenu__item', itemClass], linksClassList = ['submenu__link', linkClass];
                    links && links.length > 0 && itemClassList.push('submenu__item--has-children') && linksClassList.push('submenu__link--has-children');           
                    if (link.isSelected && !link.links.some(x => x.isSelected)) {
                        // Only add selected visual class to links that are directly selected, not their parents
                        itemClassList.push('submenu__item--selected') && linksClassList.push('submenu__link--selected');
                    }

                    return (
                        <li className={itemClassList.join(" ")} level={level} key={`${name}-${i}`}>

                            {
                                url && (
                                    <a  
                                        href={url}
                                        className={linksClassList.join(" ")}
                                        onClick={() => tcEventClickNavigationEvent(url)}
                                        level={level}>
                                        {
                                            imageUrl && 
                                                <img src={imageUrl}
                                                    alt={imageAlt || name}
                                                    className='submenu__icon' />
                                        }

                                        <span dangerouslySetInnerHTML={{ __html: name }} className='submenu__title' />

                                        {
                                            extraInfo && <span className='submenu__tagg' dangerouslySetInnerHTML={{ __html: extraInfo }}></span>
                                        }
                                    </a>
                                )
                            }

                            {
                                !url && (
                                    <button 
                                        className={linksClassList.join(" ")}
                                        onClick={() => {
                                            setIsOpen(true) // If linkUrl does not exist, open the submenu
                                            tcEventClickNavigationEvent(url)
                                        }}
                                        level={level}>
                                        {
                                            imageUrl && 
                                                <img src={imageUrl}
                                                    alt={imageAlt || name} />
                                        }

                                        <span dangerouslySetInnerHTML={{ __html: name }} className='submenu__title' />
                                        
                                        {
                                            extraInfo && <span className='submenu__tagg' dangerouslySetInnerHTML={{ __html: extraInfo }}></span>
                                        }
                                    </button>
                                )
                            }

                            {
                                links && links.length > 0 && (
                                    <Fragment>
                                        {
                                            showExpand && (
                                                <button className='submenu__expand' 
                                                    	onClick={() => {
                                                            submenuRef.current.scrollTo(0, 0)
                                                            setIsOpen(true)}
                                                        }>
                                                    <span className='submenu__expand--icon'></span>
                                                </button>
                                            )
                                        }
    
                                        <NavigationItemMobile {...{ 
                                            levelName: name, 
                                            parentLevelName: levelName, 
                                            content: link.content,
                                            links, 
                                            level: level + 1, 
                                            isOpen, 
                                            closeNavigation: () => setIsOpen(false),
                                            showExpand,
                                            showGoBack 
                                        }}/>
                                    </Fragment>
                                )
                            }
                        </li>
                    )
                })
            }

            {
                content && content.length > 0 && (
                    <div className="submenu__content">   
                        {
                            content.map((content, i) => (
                                <a href={content.url} key={i} onClick={() => { tcEventClickBrandCornerEvent('menu', content.url)} }>
                                    <div className='submenu__figure'>
                                        <img src={content.imageUrl} alt={content.title} loading='lazy' />
                                        {content.taggText && <span className='submenu__image-tag'>{content.taggText}</span>}
                                        <img src={content.iconUrl} alt={content.iconAlt} className='submenu__image-icon' />
                                    </div>
                                    <p>{content.title}</p>
                                </a>
                            ))
                        }  
                    </div>
                )
            }
        </ul>
    )
}

const NavigationItemDesktop = ({ levelName, links, level, isOpen }) => (
    <ul className="submenu__menu" level={level} open={isOpen}>     
        {
            links.map((link, i) => {
                const { links, name, url, extraInfo, itemClass, linkClass } = link;
                const itemClassList = ['submenu__item', itemClass], linksClassList = ['submenu__link', linkClass];
                links && links.length > 0 && itemClassList.push('submenu__item--has-children') && linksClassList.push('submenu__link--has-children');           
                if (link.isSelected && !link.links.some(x => x.isSelected)) {
                    // Only add selected visual class to links that are directly selected, not their parents
                    itemClassList.push('submenu__item--selected') && linksClassList.push('submenu__link--selected');
                }

                return (
                    <li className={itemClassList.join(" ")} level={level} key={`${name}-${i}`}>
                        <a  href={url}
                            className={linksClassList.join(" ")}
                            onClick={() => tcEventClickNavigationEvent(url)}
                            level={level}
                            dangerouslySetInnerHTML={{ __html: name }}>
                        </a>

                        {
                            extraInfo && <span className='submenu__tagg' dangerouslySetInnerHTML={{ __html: extraInfo }}></span>
                        }
                        
                        {
                            links && links.length > 0 && (
                                <NavigationItemDesktop {...{ 
                                    levelName: name, 
                                    parentLevelName: levelName, 
                                    links, 
                                    level: level + 1, 
                                    isOpen, 
                                    closeNavigation: () => setIsOpen(false)
                                }}/>
                            )
                        }
                    </li>
                )
            })
        }
    </ul>
)


export default Navigation;